import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, ArrowRight, Star, Clock } from 'lucide-react';
import Button from '../../../components/ui/Button';

const RecommendationEngine = ({ currentUser, roadmaps }) => {
  // Generate personalized recommendations based on user profile
  const getRecommendations = () => {
    if (!currentUser) {
      // Default recommendations for anonymous users
      return [
        {
          title: 'Start with Manual Testing',
          reason: 'Perfect foundation for beginners',
          roadmap: roadmaps?.find(r => r?.slug === 'manual-testing')
        },
        {
          title: 'Popular Choice: Python Automation',
          reason: 'Most enrolled by new learners',
          roadmap: roadmaps?.find(r => r?.slug === 'automation-testing-python')
        }
      ];
    }

    // Mock user skill analysis - in real app, this would be based on user's profile, assessments, etc.
    const userSkills = currentUser?.skills || [];
    const userExperience = currentUser?.experience || 'beginner';
    const userGoals = currentUser?.goals || [];

    let recommendations = [];

    // Experience-based recommendations
    if (userExperience === 'beginner') {
      recommendations?.push({
        title: 'Manual Testing Foundation',
        reason: 'Essential starting point for all testers',
        roadmap: roadmaps?.find(r => r?.slug === 'manual-testing')
      });
    } else if (userExperience === 'intermediate') {
      recommendations?.push({
        title: 'API Testing Mastery',
        reason: 'High demand skill in current market',
        roadmap: roadmaps?.find(r => r?.slug === 'api-testing')
      });
    }

    // Skill-based recommendations
    if (userSkills?.includes('java')) {
      recommendations?.push({
        title: 'Java Automation Testing',
        reason: 'Leverage your Java knowledge',
        roadmap: roadmaps?.find(r => r?.slug === 'automation-testing-java')
      });
    }

    if (userSkills?.includes('python')) {
      recommendations?.push({
        title: 'Python Test Automation',
        reason: 'Build on your Python expertise',
        roadmap: roadmaps?.find(r => r?.slug === 'automation-testing-python')
      });
    }

    // Goal-based recommendations
    if (userGoals?.includes('performance')) {
      recommendations?.push({
        title: 'Performance Testing',
        reason: 'Aligns with your career goals',
        roadmap: roadmaps?.find(r => r?.slug === 'performance-testing')
      });
    }

    // Fallback to popular roadmaps if no specific matches
    if (recommendations?.length === 0) {
      const popularRoadmaps = roadmaps
        ?.sort((a, b) => b?.enrolled - a?.enrolled)
        ?.slice(0, 2);
      
      recommendations = popularRoadmaps?.map(roadmap => ({
        title: roadmap?.title,
        reason: 'Popular choice among learners',
        roadmap
      }));
    }

    return recommendations?.slice(0, 3); // Limit to 3 recommendations
  };

  let recommendations = getRecommendations();

  if (!recommendations?.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border rounded-lg p-6 mb-6"
    >
      <div className="flex items-center space-x-2 mb-4">
        <Lightbulb className="w-5 h-5 text-yellow-500" />
        <h3 className="font-semibold text-lg">
          {currentUser ? 'Recommended for You' : 'Popular Recommendations'}
        </h3>
      </div>
      <div className="space-y-4">
        {recommendations?.map((rec, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="border border-border rounded-lg p-4 hover:border-blue-300 transition-colors group"
          >
            {rec?.roadmap && (
              <>
                <div className="flex items-start space-x-3 mb-3">
                  <div className={`w-10 h-10 ${rec?.roadmap?.color} rounded-lg flex items-center justify-center text-white group-hover:scale-105 transition-transform`}>
                    {rec?.roadmap?.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm group-hover:text-blue-600 transition-colors">
                      {rec?.title}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {rec?.reason}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      rec?.roadmap?.difficulty === 'Beginner' ? 'bg-green-100 text-green-600' :
                      rec?.roadmap?.difficulty === 'Intermediate' ? 'bg-blue-100 text-blue-600' :
                      rec?.roadmap?.difficulty === 'Advanced'? 'bg-purple-100 text-purple-600' : 'bg-red-100 text-red-600'
                    }`}>
                      {rec?.roadmap?.difficulty}
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock className="w-3 h-3" />
                      <span>{rec?.roadmap?.estimatedTime}</span>
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Star className="w-3 h-3 text-yellow-500" />
                    <span>{rec?.roadmap?.rating}</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full flex items-center justify-center space-x-2 group-hover:bg-blue-50 group-hover:border-blue-300"
                  onClick={() => {
                    // Navigate to roadmap details
                    window.location.href = `/roadmap/${rec?.roadmap?.slug}`;
                  }}
                >
                  <span>Explore Path</span>
                  <ArrowRight className="w-3 h-3" />
                </Button>
              </>
            )}
          </motion.div>
        ))}
      </div>
      {currentUser && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            Recommendations based on your profile and learning goals. 
            <button className="text-blue-600 hover:underline ml-1">
              Update preferences
            </button>
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default RecommendationEngine;